/**
 * Right-side drawer for a whole conversation, rendered as a chat transcript.
 *
 * Clicking any exchange stacks the run drawer on top of this one, so you can
 * read the conversation, drop into a single run's trace, and come back without
 * losing your place. Same shell as the run and activity drawers.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { openUserProfile } from 'features/users/openUserProfile';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import DrawerScrim, { DRAWER_OVERLAY_CLASS } from 'shared/components/ui/DrawerScrim';
import MetaGrid from 'shared/components/ui/MetaGrid';
import ConversationChat from 'features/agent-analytics/components/ConversationChat';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import TraceDetailPage from 'features/agent-analytics/pages/TraceDetailPage';
import { AnnotationList, useAnnotations } from 'features/agent-analytics/annotations';
import { getConversationAnnotationCount } from 'features/agent-analytics/annotations/annotationSelectors';
import { getConversation } from 'features/agent-analytics/api/conversationsApi';
import Eval3VerdictsPanel, { useEval3Count } from 'features/evals3/components/Eval3VerdictsPanel';
import TargetSignals from 'features/issues/components/TargetSignals';
import { searchConversation } from 'features/agent-analytics/utils/conversationSearch';
import {
  formatElapsed, formatCost, formatDate, formatNumber, truncate, ScorePill,
} from 'features/agent-analytics/components/runCells';

export default function ConversationDetailDrawer({
  conversationId,
  teamId,
  /** Session window from the list row — bounds this drawer to one sitting. */
  from,
  to,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  initialTab,
}) {
  const [runs, setRuns] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // The run drawer stacked over this one.
  const [openRunId, setOpenRunId] = useState(null);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState(initialTab || 'transcript');
  const [openSpanId, setOpenSpanId] = useState(null);
  const [stackedTab, setStackedTab] = useState('activity');
  const [signalsCount, setSignalsCount] = useState(0);
  const { annotations, enabled: annotationsEnabled } = useAnnotations();
  const annotationCount = getConversationAnnotationCount(annotations, conversationId);
  // Counts for the tab badges. Fetched here rather than inside the panels so a
  // number is on the tab before you open it — the whole point of a badge is to
  // tell you whether the tab is worth a click.
  // The refresh key the eval3 panel and its count share. Nothing bumps it
  // since the old evals' grading list came out of this drawer (TRO-475).
  const [gradeTick] = useState(0);

  const load = useCallback(async () => {
    if (!conversationId || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getConversation({ teamId, conversationId, from, to });
      setConversation(data.conversation);
      setRuns(data.runs);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load conversation.');
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [conversationId, teamId, from, to]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setTab(initialTab || 'transcript');
    setOpenRunId(null);
    setOpenSpanId(null);
    setSignalsCount(0);
  }, [conversationId, initialTab]);

  // Esc closes the conversation only when no run is stacked over it — otherwise
  // the run drawer's own handler takes the key and closes just itself.
  useEffect(() => {
    if (openRunId || openSpanId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, openRunId, openSpanId]);

  const openAnnotationContext = useCallback((ctx) => {
    if (!ctx || ctx.type === 'conversation') return;
    if (ctx.type === 'run') {
      setOpenSpanId(null);
      setOpenRunId(ctx.id);
      setStackedTab('activity');
      return;
    }
    if (ctx.type === 'span') {
      setOpenRunId(null);
      setOpenSpanId(ctx.id);
      setStackedTab('activity');
    }
  }, []);

  // Before the early return: a hook runs on every render.
  const v3Count = useEval3Count(teamId, 'conversation', conversationId, gradeTick);

  if (!conversationId) return null;

  const { runs: visibleRuns, matchedCount } = searchConversation(runs, query);
  const c = conversation;
  // Sum, matching the run drawer's Scores badge. An earlier version showed
  // `pending || scored` on the theory that work owed outranks work done — but
  // a badge counts what is behind the tab, and that read "1" on a tab holding
  // three scores and a pending grade.
  const scoresBadge = v3Count || null;

  const metaCells = [
    {
      label: 'Conversation',
      title: String(conversationId),
      content: <span className="font-mono">{truncate(String(conversationId), 18)}</span>,
    },
    c?.run_count != null && { label: 'Runs', content: <span className="tabular-nums">{c.run_count}</span> },
    c?.error_count ? {
      label: 'Errors',
      content: <span className="tabular-nums text-[color:var(--feedback-error)]">{c.error_count}</span>,
    } : null,
    c?.agents?.length && {
      label: c.agents.length > 1 ? 'Agents' : 'Agent',
      title: c.agents.join(', '),
      content: <span>{c.agents.join(', ')}</span>,
    },
    c?.first_run_at && {
      label: 'Started',
      title: formatDate(c.first_run_at),
      content: <span className="tabular-nums">{formatDate(c.first_run_at)}</span>,
    },
    c?.span_ms != null && {
      label: 'Elapsed',
      content: <span className="tabular-nums">{formatElapsed(c.span_ms)}</span>,
    },
    (c?.total_tokens_in || c?.total_tokens_out) && {
      label: 'Tokens',
      content: (
        <span className="tabular-nums" title="in / out">
          {formatNumber(c.total_tokens_in)} / {formatNumber(c.total_tokens_out)}
        </span>
      ),
    },
    c?.total_cost ? { label: 'Cost', content: <span className="tabular-nums">{formatCost(c.total_cost)}</span> } : null,
    c?.distinct_id && {
      label: 'User',
      title: c.distinct_id,
      // Opens the user's drawer, same as the run drawer's User cell.
      content: (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); openUserProfile(c.distinct_id); }}
          className="cursor-pointer border-0 bg-transparent p-0 font-mono text-[color:var(--text-primary)] underline-offset-2 hover:underline"
        >
          {truncate(c.distinct_id, 18)}
        </button>
      ),
    },
    c?.satisfaction_score != null && {
      label: 'Sat (avg)',
      content: <ScorePill value={c.satisfaction_score} kind="sat" />,
    },
  ].filter(Boolean);

  const iconBtn =
    'flex h-7 w-7 flex-none items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-30';

  return (
    <>
      <div className={DRAWER_OVERLAY_CLASS} role="region" aria-label="Conversation detail">
        <DrawerScrim onClose={onClose} />
        <section
          className="pointer-events-auto relative flex h-full w-[min(92%,880px)] flex-col overflow-hidden border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 px-4 pt-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <button type="button" onClick={onPrev} disabled={!hasPrev} title="Previous conversation" className={iconBtn}>
                  <ChevronLeft size={16} />
                </button>
                <button type="button" onClick={onNext} disabled={!hasNext} title="Next conversation" className={iconBtn}>
                  <ChevronRight size={16} />
                </button>
              </div>
              {/* Search replaces the old static "Conversation" label: the word
                  restated the drawer's own heading and earned none of its row. */}
              <div className="relative min-w-0 flex-1">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]"
                />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape' && query) { e.stopPropagation(); setQuery(''); } }}
                  placeholder="Search this conversation: input & output…"
                  className="h-8 w-full rounded-lg bg-[color:var(--surface-sunken)] pl-8 pr-7 text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--trodo-primary)]"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                    className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                  >
                    <X size={13} />
                  </button>
                ) : null}
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className={iconBtn}>
                <X size={16} />
              </button>
            </div>

            {metaCells.length ? <MetaGrid cells={metaCells} columns={4} /> : null}

            {/* Same three-tab shell the run drawer uses, for the same reason:
                a conversation has more to say than one panel holds, and the
                scores in particular are a claim ABOUT the transcript, so they
                want their own surface rather than a strip squeezed above it. */}
            <div className="-mx-4 flex min-h-0 flex-1 flex-col overflow-hidden border-t border-[color:var(--border-default)] bg-surface">
              <div className="flex flex-none items-center gap-1 border-b border-[color:var(--border-default)] bg-surface px-3">
                {/* Two tabs, not three. An "Activity" tab listing the runs in
                    the conversation was the transcript again with less in it —
                    the exchanges ARE the activity. */}
                <ConvTab id="transcript" label="Transcript" active={tab === 'transcript'} onClick={setTab}
                         badge={runs.length || null} />
                <ConvTab id="scores"     label="Scores"     active={tab === 'scores'}     onClick={setTab}
                         badge={scoresBadge} />
                {/* A tab here, a section in Activity on runs and spans. The
                    reason is the tab strip: runs already carried five and a
                    sixth overflowed, while this one has room: and a
                    conversation's comments are every remark on it and on all
                    its runs and spans, which is a view in its own right
                    rather than a footnote to the transcript. */}
                <ConvTab id="signals"    label="Signals"    active={tab === 'signals'}    onClick={setTab}
                         badge={signalsCount || null} />
                {annotationsEnabled ? (
                  <ConvTab id="annotations" label="Comments" active={tab === 'annotations'} onClick={setTab}
                           badge={annotationCount || null} />
                ) : null}
                {/* Search belongs to the transcript, so it leaves with it. */}
                {tab === 'transcript' && query ? (
                  <span className="ml-auto pr-2 text-[11px] text-[color:var(--text-muted)]">
                    {matchedCount} of {runs.length} match
                  </span>
                ) : null}
              </div>

              <div className={`min-h-0 flex-1 ${tab === 'annotations' ? 'flex min-h-0 flex-col overflow-hidden' : 'overflow-y-auto'}`}>
                {tab === 'transcript' ? (
                  <ConversationChat
                    runs={visibleRuns}
                    loading={loading}
                    error={error}
                    activeRunId={openRunId}
                    onOpenRun={(id) => {
                      setStackedTab('activity');
                      setOpenRunId(id);
                    }}
                    emptyMessage={query ? `No exchange in this conversation matches “${query}”.` : undefined}
                  />
                ) : tab === 'signals' ? (
                  <div className="p-3">
                    <TargetSignals
                      teamId={teamId}
                      kind="conversation"
                      id={conversationId}
                      onCount={setSignalsCount}
                      onSpanClick={(id) => { setStackedTab('activity'); setOpenRunId(null); setOpenSpanId(id); }}
                      onRunClick={(id) => { setStackedTab('activity'); setOpenRunId(id); }}
                    />
                  </div>
                ) : tab === 'annotations' ? (
                  <AnnotationList
                    conversationId={conversationId}
                    scope="all"
                    lockedContext={{ type: 'conversation' }}
                    onOpenContext={openAnnotationContext}
                  />
                ) : (
                  <div className="flex flex-col gap-5 p-3">
                    {/* The old evals' Pending and Evaluations sections stood
                        here until TRO-475; the UI is off, the engine and its
                        data are untouched. */}
                    {/* The new evals' verdicts on the thread and on each of its runs. */}
                    <Eval3VerdictsPanel teamId={teamId} target="conversation" unitId={conversationId} refreshKey={gradeTick} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Stacked run drawer. It renders its own scrim, so closing it returns to
          the transcript with this drawer still open behind. */}
      {openSpanId ? (
        <TraceDetailPage
          spanId={openSpanId}
          teamId={teamId}
          initialTab={stackedTab}
          onClose={() => setOpenSpanId(null)}
          onRunClick={(runId) => {
            setOpenSpanId(null);
            setOpenRunId(runId);
            setStackedTab('activity');
          }}
        />
      ) : null}

      {openRunId ? (
        <AgentRunDetailPage
          runId={openRunId}
          teamId={teamId}
          initialTab={stackedTab}
          onClose={() => setOpenRunId(null)}
        />
      ) : null}
    </>
  );
}

/**
 * Tab button for the conversation drawer.
 *
 * Visually identical to the run drawer's TabButton on purpose — the two drawers
 * stack on top of each other, and a tab bar that changed shape between them
 * would read as a different kind of surface rather than the same one at a
 * different scope.
 */
function ConvTab({ id, label, badge, active, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      aria-current={active ? 'page' : undefined}
      className={`relative px-3 py-2 text-[12px] font-medium transition-colors ${
        active ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {badge != null ? (
          <span className={`rounded-full px-1.5 text-[10px] font-semibold ${
            active ? 'bg-surface-hover text-[color:var(--text-primary)]' : 'bg-[color:var(--border-subtle)] text-[color:var(--text-secondary)]'
          }`}>
            {badge}
          </span>
        ) : null}
      </span>
      <span className={`absolute inset-x-2 bottom-0 h-[2px] rounded-t ${active ? 'bg-[color:var(--text-primary)]' : 'bg-transparent'}`} />
    </button>
  );
}
